import React, { useMemo } from 'react'
import { kpiMode, useSourceView } from './UseSourceView.ts'
import type { SinkConfig, SinkId, SourceId } from '../../../../Flux/Slice/Visualization/VisualizationSliceTypes.js'
import { isLoadingState } from '../../../../Flux/Slice/Visualization/VisualizationSliceTypes.js'
import { RankedBarChartNext } from '../../../Component/Charts/RankedBarChartNext.tsx'
import { useTransformer } from '../Transformer/UseTransformer.tsx'
import { useBreakdownSinkTable } from '../DataTable/UseSinkTable.ts'
import { rankTopTaxa } from './Selector/RankTaxa.ts'

export type RankedBarParams = { topN?: number; title?: string; rankLevel?: string }

const EMPTY_PATH: string[] = []
const DEFAULT_TOP_N = 10

/** Human label for what the bars represent, matching the aggregation mode picked in `aggregateRankedBar`. */
function rankedNoun(inTraitMode: boolean, rankLevel: string | undefined, selectedPath: string[]): string {
  if (inTraitMode) return 'trait groups'
  if (rankLevel) return `${rankLevel.toLowerCase()}-rank taxa`
  if (selectedPath.length > 0) return `${selectedPath[selectedPath.length - 1]} sub-taxa`
  return 'organisms'
}

/**
 * Ranked Bar: top-N most-abundant ORGANISMS by reads. Single-bucket, like Pie/Treemap — but it ranks
 * the DEEPEST-resolved taxon per row (the leaf organism), NOT the child-at-current-level. It consumes
 * `taxaLeafAggregationSingle` (leaf → reads, already scoped to the current zoom via selectedPath), then
 * `rankTopTaxa` sorts descending and slices the top `topN`. At the taxonomy root it ranks organisms
 * across everything; zoomed into a group (via taxonEntry/selectedPath) it ranks that group's organisms —
 * matching the "most abundant organisms" table/digest.
 *
 * Bars are NOT clickable: a leaf is already the deepest-resolved taxon, so there is nothing further to
 * drill into (unlike a pie slice / treemap node, which is a level-child).
 */
export default function RankedBarSink({
  sourceId,
  bucketKey,
  config
}: {
  sourceId: SourceId
  sinkId: SinkId
  bucketKey?: string
  config: SinkConfig<RankedBarParams>
}) {
  const { snapshot, shared, status } = useSourceView(sourceId)

  const selectedPath = shared?.selectedPath ?? EMPTY_PATH
  const gradeThreshold = shared?.gradeThreshold ?? 0
  const scalingMode = shared?.scalingMode ?? 'ABSOLUTE'
  const topN = config.params?.topN ?? DEFAULT_TOP_N
  // Optional rank NAME (domain…species). When set, the ranking forces onto that uniform rank; when
  // absent, the aggregation picks entry-children (zoomed) or leaf organisms (root) automatically.
  const rankLevel = config.params?.rankLevel

  // Nature-KPI mode: rank by trait group instead of organism. Trait groups are already terminal
  // categories (no leaf concept), so we keep `traitAggregationSingle`; the ranking below is mode-agnostic.
  const { inTraitMode, traitGroup, noTraitMode } = kpiMode(shared)

  const aggRes = inTraitMode
    ? useTransformer(sourceId, bucketKey, 'traitAggregationSingle', { selectedPath, gradeThreshold, traitGroup, scalingMode, noTraitMode })
    : useTransformer(sourceId, bucketKey, 'taxaRankedBarSingle', { selectedPath, gradeThreshold, rankLevel })

  const data = useMemo(() => {
    if (aggRes.status !== 'ready') return []
    return rankTopTaxa(aggRes.value, topN)
  }, [aggRes, topN])

  // The slices as numbers, published to the container's "Numbers" view. Same entries the chart
  // draws, so the table cannot drift from it.
  useBreakdownSinkTable({
    sourceId,
    bucketKey,
    selectedPath,
    gradeThreshold,
    inTraitMode,
    ready: aggRes.status === 'ready' && data.length > 0,
    entries: data,
    withTaxaCounts: !inTraitMode && !rankLevel && selectedPath.length > 0,
    categoryLabel: inTraitMode ? 'Trait group' : 'Taxon',
    valueLabel: scalingMode === 'PERCENTAGE' ? 'Share (%)' : String(shared?.countMode ?? 'Reads')
  })

  if (!snapshot) {
    return (
      <div className="text-sm opacity-70">
        Waiting for data… {isLoadingState(status) && <span className="loading loading-dots loading-xs" />}
      </div>
    )
  }

  if (aggRes.status === 'idle' || aggRes.status === 'loading') {
    return (
      <div className="text-sm opacity-70">Computing… {isLoadingState(status) && <span className="loading loading-dots loading-xs" />}</div>
    )
  }

  if (aggRes.status === 'error') {
    return <div className="text-sm opacity-70">Error computing ranked bar.</div>
  }

  if (data.length === 0) {
    return <div className="text-sm opacity-70">No data for current selection.</div>
  }

  const bars = data.map((entry) => ({ name: entry.name, value: entry.value }))

  return (
    <div className="flex flex-col gap-2 h-full">
      <div className="text-xs opacity-70">
        Ranked Bar: top {topN} {rankedNoun(inTraitMode, rankLevel, selectedPath)} by reads
      </div>
      <div className="w-full h-full">
        <RankedBarChartNext data={bars} height="100%" />
      </div>
    </div>
  )
}
